import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Splide, SplideSlide, SplideTrack, Options } from '@splidejs/react-splide';
import { Image, ArrowL16 as IconChevronLeft16, ArrowR16 as IconChevronRight16 } from '@cian/ui-kit';

import {
  IExtractAndReplacePhotoGalleryItem,
  IExtractAndReplacePhotoGallerySlideItem,
} from '../../containers/post_card/helpers';
import * as s from './PostPhotoGallery.css';

export interface IPostPhotoGallery {
  slides: IExtractAndReplacePhotoGallerySlideItem[];
  source?: IExtractAndReplacePhotoGalleryItem['source'];
  description?: IExtractAndReplacePhotoGalleryItem['description'];
}

/**
 * Фотогалерея в теле поста
 */
export const PostPhotoGallery = ({ slides, source, description }: IPostPhotoGallery) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const mainGalleryRef = useRef<Splide>(null);
  const thumbsGalleryRef = useRef<Splide>(null);

  useEffect(() => {
    if (mainGalleryRef?.current && thumbsGalleryRef?.current?.splide) {
      mainGalleryRef.current.sync(thumbsGalleryRef.current.splide);
    }
  }, []);

  const mainGalleryOptions: Options = {
    cover: true,
    heightRatio: 0.7,
    gap: 10,
    pagination: false,
    rewind: true,
    wheel: true,
    releaseWheel: false,
    wheelSleep: 800,
    drag: true,
    keyboard: true,
  };

  const thumbsGalleryOptions: Options = {
    arrows: false,
    fixedWidth: 64,
    fixedHeight: 64,
    gap: 8,
    isNavigation: true,
    pagination: false,
    keyboard: true,
  };

  const getCurrentDescription = useCallback(() => {
    return slides[activeIndex].description || description;
  }, [activeIndex, description, slides]);

  const getCurrentSource = useCallback(() => {
    return slides[activeIndex].source || source;
  }, [activeIndex, source, slides]);

  return (
    <div className={s['wrapper']}>
      <Splide
        ref={mainGalleryRef}
        className={s['main-gallery']}
        hasTrack={false}
        aria-label={description}
        options={mainGalleryOptions}
        onMounted={gallery => setActiveIndex(gallery.index)}
        onMove={gallery => setActiveIndex(gallery.index)}
      >
        <SplideTrack>
          {slides.map(({ image }, index) => (
            <SplideSlide key={index} className={s['slide']} tabIndex={index}>
              <Image src={image} borderRadius={0} objectFit={'contain'} />
            </SplideSlide>
          ))}
        </SplideTrack>

        <div className={`splide__arrows ${s['arrows']}`}>
          <button className={`splide__arrow splide__arrow--prev ${s['arrow']} ${s['arrow-prev']}`}>
            <IconChevronLeft16 color={'white_100'} />
          </button>

          <button className={`splide__arrow splide__arrow--next ${s['arrow']} ${s['arrow-next']}`}>
            <IconChevronRight16 color={'white_100'} />
          </button>
        </div>
      </Splide>

      <Splide
        ref={thumbsGalleryRef}
        className={s['thumbs-gallery']}
        hasTrack={false}
        aria-label="My Favorite Images"
        options={thumbsGalleryOptions}
      >
        <SplideTrack>
          {slides.map(({ image }, index) => (
            <SplideSlide
              key={index}
              className={`${s['slide']} ${activeIndex === index ? s['is-active'] : ''}`}
              tabIndex={index}
            >
              <Image src={image} borderRadius={0} objectFit={'cover'} />
            </SplideSlide>
          ))}
        </SplideTrack>
      </Splide>

      <div>{getCurrentDescription()}</div>
      <div>{getCurrentSource()}</div>
    </div>
  );
};
